import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, ChevronRight, Server, DollarSign, HelpCircle, HeadphonesIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Department } from './admin/DepartmentManager';
import { useAppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
interface ChatMessage {
  id: string;
  sender: 'bot' | 'user' | 'system';
  content: string | React.ReactNode;
  timestamp: Date;
}
export function ChatWidget() {
  const {
    staff
  } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [chatState, setChatState] = useState<'welcome' | 'departments' | 'chatting'>('welcome');
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    loadDepartments();
    // Initial welcome message
    setMessages([{
      id: 'msg_welcome',
      sender: 'bot',
      content: 'Hi! 👋 Welcome to Fox Line Hosting. How can we help you today?',
      timestamp: new Date()
    }]);
  }, []);
  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      scrollToBottom();
    }
  }, [isOpen, messages]);
  const loadDepartments = async () => {
    try {
      const {
        data
      } = await supabase.from('site_settings').select('value').eq('key', 'departments').maybeSingle();
      if (data?.value) {
        setDepartments((data.value as Department[]).filter(d => d.isActive));
      }
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const addMessage = (sender: 'bot' | 'user' | 'system', content: string | React.ReactNode) => {
    setMessages(prev => [...prev, {
      id: `msg_${Date.now()}`,
      sender,
      content,
      timestamp: new Date()
    }]);
  };
  const handleQuickAction = (action: string) => {
    addMessage('user', action);
    setTimeout(() => {
      switch (action) {
        case 'Pricing & Plans':
          addMessage('bot', <div data-id="element-1545">
              <p className="mb-2" data-id="element-1546">
                We offer a variety of hosting solutions starting at just
                $9.99/mo.
              </p>
              <Link to="/#pricing" className="text-fox-500 hover:underline font-medium flex items-center gap-1" onClick={() => setIsOpen(false)} data-id="element-1547">
                View all plans <ChevronRight size={14} data-id="element-1548" />
              </Link>
            </div>);
          break;
        case 'Server Status':
          addMessage('bot', <div data-id="element-1549">
              <p className="mb-2" data-id="element-1550">
                All systems are currently operational. You can check live status
                and maintenance history on our status page.
              </p>
              <Link to="/status" className="text-fox-500 hover:underline font-medium flex items-center gap-1" onClick={() => setIsOpen(false)} data-id="element-1551">
                Check Server Status <ChevronRight size={14} data-id="element-1552" />
              </Link>
            </div>);
          break;
        case 'Account Help':
          addMessage('bot', 'To manage your account, invoices, or services, please log in to your dashboard. If you forgot your password, use the "Forgot Password" link on the login page.');
          break;
        case 'Connect to Agent':
          setChatState('departments');
          addMessage('system', 'Please select a department to route your chat:');
          break;
      }
    }, 600);
  };
  const handleSelectDepartment = (dept: Department) => {
    setSelectedDept(dept);
    setChatState('chatting');
    addMessage('user', `Connect to ${dept.name}`);
    setTimeout(() => {
      addMessage('system', `Connecting you to ${dept.name}...`);
      setTimeout(() => {
        addMessage('bot', `An agent from ${dept.name} will be with you shortly. Please describe your issue while you wait.`);
      }, 1000);
    }, 500);
  };
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const userMsg = inputValue.trim();
    addMessage('user', userMsg);
    setInputValue('');
    // Simple auto-responder for demo purposes
    if (chatState === 'chatting') {
      setTimeout(() => {
        addMessage('bot', `Thanks for reaching out! A ${selectedDept?.name || 'support'} agent will respond shortly. Average wait time: ~5 minutes.`);
      }, 1500);
    }
  };
  const getDeptStaffCount = (dept: Department) => {
    return staff.filter(s => dept.staffIds?.includes(s.id)).length;
  };
  return <>
      {/* Floating Button */}
      <motion.button initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setIsOpen(true)} className={`fixed bottom-6 right-6 w-14 h-14 bg-fox-600 hover:bg-fox-500 text-white rounded-full shadow-lg shadow-fox-500/30 flex items-center justify-center z-50 transition-colors ${isOpen ? 'hidden' : 'flex'}`} data-id="element-1553">
        <MessageCircle size={24} data-id="element-1554" />
        {hasUnread && <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-red-500 border-2 border-slate-950 rounded-full" data-id="element-1555"></span>}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence data-id="element-1556">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-6 right-6 w-[380px] h-[600px] max-h-[80vh] bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden" data-id="element-1557">
            {/* Header */}
            <div className="bg-slate-900 border-b border-slate-800 p-4 flex items-center justify-between shrink-0" data-id="element-1558">
              <div className="flex items-center gap-3" data-id="element-1559">
                <div className="relative" data-id="element-1560">
                  <div className="w-10 h-10 bg-fox-500/10 rounded-full flex items-center justify-center" data-id="element-1561">
                    <Bot size={20} className="text-fox-500" data-id="element-1562" />
                  </div>
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-slate-900 rounded-full" data-id="element-1563"></div>
                </div>
                <div data-id="element-1564">
                  <h3 className="text-white font-bold text-sm" data-id="element-1565">
                    Fox Line Support
                  </h3>
                  <p className="text-xs text-slate-400" data-id="element-1566">
                    We typically reply in minutes
                  </p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-white hover:bg-slate-800 transition-colors p-1.5 rounded-lg" aria-label="Close chat" title="Close chat" data-id="element-1567">
                <X size={22} data-id="element-1568" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950/50" data-id="element-1569">
              {messages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : msg.sender === 'system' ? 'items-center' : 'items-start'}`} data-id="element-1570">
                  {msg.sender === 'system' ? <div className="text-xs font-medium text-slate-500 bg-slate-900 px-3 py-1 rounded-full my-2" data-id="element-1571">
                      {msg.content}
                    </div> : <div className={`flex items-end gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`} data-id="element-1572">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${msg.sender === 'user' ? 'bg-slate-800' : 'bg-fox-500/20'}`} data-id="element-1573">
                        {msg.sender === 'user' ? <User size={12} className="text-slate-400" data-id="element-1574" /> : <Bot size={12} className="text-fox-500" data-id="element-1575" />}
                      </div>
                      <div className={`px-4 py-2.5 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-fox-600 text-white rounded-br-sm' : 'bg-slate-800 text-slate-200 rounded-bl-sm'}`} data-id="element-1576">
                        {msg.content}
                      </div>
                    </div>}
                </div>)}

              {/* Quick Actions / Department Selection */}
              {chatState === 'welcome' && <div className="flex flex-col gap-2 mt-4 pl-8" data-id="element-1577">
                  <button onClick={() => handleQuickAction('Pricing & Plans')} className="text-left px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-sm text-slate-300 transition-colors flex items-center gap-2" data-id="element-1578">
                    <DollarSign size={14} className="text-emerald-500" data-id="element-1579" />{' '}
                    Pricing & Plans
                  </button>
                  <button onClick={() => handleQuickAction('Server Status')} className="text-left px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-sm text-slate-300 transition-colors flex items-center gap-2" data-id="element-1580">
                    <Server size={14} className="text-blue-500" data-id="element-1581" /> Server Status
                  </button>
                  <button onClick={() => handleQuickAction('Account Help')} className="text-left px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl text-sm text-slate-300 transition-colors flex items-center gap-2" data-id="element-1582">
                    <HelpCircle size={14} className="text-purple-500" data-id="element-1583" /> Account
                    Help
                  </button>
                  <button onClick={() => handleQuickAction('Connect to Agent')} className="text-left px-4 py-2 bg-fox-500/10 hover:bg-fox-500/20 border border-fox-500/20 rounded-xl text-sm text-fox-400 font-medium transition-colors flex items-center gap-2 mt-2" data-id="element-1584">
                    <HeadphonesIcon size={14} data-id="element-1585" /> Connect to Agent
                  </button>
                </div>}

              {chatState === 'departments' && <div className="flex flex-col gap-2 mt-4 pl-8" data-id="element-1586">
                  {departments.length === 0 ? <div className="text-sm text-slate-500 text-center py-2" data-id="element-1587">
                      No departments available currently.
                    </div> : departments.map(dept => {
              const count = getDeptStaffCount(dept);
              return <button key={dept.id} onClick={() => handleSelectDepartment(dept)} className="text-left px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-xl transition-colors flex flex-col gap-1" data-id="element-1588">
                          <span className="text-sm font-medium text-white" data-id="element-1589">
                            {dept.name}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-1590">
                            <div className={`w-1.5 h-1.5 rounded-full ${count > 0 ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-1591"></div>
                            {count > 0 ? `${count} agents online` : 'Leave a message'}
                          </span>
                        </button>;
            })}
                </div>}

              <div ref={messagesEndRef} data-id="element-1592" />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-800 bg-slate-900 shrink-0" data-id="element-1593">
              <form onSubmit={handleSendMessage} className="flex gap-2 relative" data-id="element-1594">
                <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={chatState === 'chatting' ? 'Type your message...' : 'Select an option above'} disabled={chatState !== 'chatting'} className="w-full bg-slate-950 border border-slate-800 rounded-full pl-4 pr-12 py-3 text-sm text-white focus:outline-none focus:border-fox-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1595" />
                <button type="submit" disabled={!inputValue.trim() || chatState !== 'chatting'} className="absolute right-1.5 top-1.5 bottom-1.5 w-9 bg-fox-600 hover:bg-fox-500 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1596">
                  <Send size={14} className="ml-0.5" data-id="element-1597" />
                </button>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}